import {useState, useCallback, useRef} from 'react';
import {observer} from 'mobx-react';
import {APIProvider, Map, AdvancedMarker, Pin, useMap} from '@vis.gl/react-google-maps';
import {TimeTrackingSubnav} from 'main/containers/time_tracking';
import {Layout} from 'shared/components';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const DEFAULT_CENTER = {lat: 43.6532, lng: -79.3832};
const DEFAULT_ZOOM = 12;
const RADIUS_METERS = 50;

function haversineDistance(a, b) {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sin2Lat = Math.sin(dLat / 2) ** 2;
  const sin2Lng = Math.sin(dLng / 2) ** 2;
  const h = sin2Lat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sin2Lng;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function GeocodingMap() {
  const map = useMap();
  const circleRef = useRef(null);
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [myLocation, setMyLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  function updateCircle(coords) {
    if (!circleRef.current) {
      circleRef.current = new google.maps.Circle({
        map,
        radius: RADIUS_METERS,
        fillColor: '#4285F4',
        fillOpacity: 0.2,
        strokeColor: '#4285F4',
        strokeOpacity: 0.5,
        strokeWeight: 1,
      });
    }
    circleRef.current.setCenter(coords);
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!address.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();

      if (data.status === 'OK') {
        const {lat, lng} = data.results[0].geometry.location;
        setCoordinates({lat, lng});
        updateCircle({lat, lng});
        map?.panTo({lat, lng});
      } else {
        setError(`Geocoding failed: ${data.status}`);
      }
    } catch (err) {
      setError('Failed to fetch coordinates');
    } finally {
      setLoading(false);
    }
  }

  function handleMyLocation() {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLocating(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setMyLocation({lat: position.coords.latitude, lng: position.coords.longitude, acc: position.coords.accuracy});
        setLocating(false);
      },
      () => {
        setError('Unable to retrieve your location');
        setLocating(false);
      }
    );
  }

  const handleMapClick = useCallback((e) => {
    const latLng = e.detail.latLng;
    if (!latLng) return;
    const coords = {lat: latLng.lat, lng: latLng.lng};
    setCoordinates(coords);
    updateCircle(coords);
    setError(null);
  }, []);

  return (
    <div>
      <form onSubmit={handleSearch} style={{display: 'flex', gap: '8px', marginBottom: '16px'}}>
        <input
          type='text'
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder='Enter an address...'
          style={{flex: 1, padding: '8px', fontSize: '14px'}}
        />
        <button type='submit' disabled={loading} style={{padding: '8px 16px'}}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>
      {coordinates && (
        <div style={{marginBottom: '16px'}}>
          <p><strong>Latitude:</strong> {coordinates.lat} &nbsp; <strong>Longitude:</strong> {coordinates.lng}</p>
        </div>
      )}
      {error && <p style={{color: 'red', marginBottom: '16px'}}>{error}</p>}
      <Map
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        onClick={handleMapClick}
        mapId='geocoding-map'
        style={{width: '100%', height: '500px'}}
      >
        {coordinates && (
          <AdvancedMarker position={coordinates}>
            <Pin/>
          </AdvancedMarker>
        )}
      </Map>
      <button onClick={handleMyLocation} disabled={locating} style={{marginTop: '16px', padding: '8px 16px'}}>
        {locating ? 'Locating...' : 'Use My Location'}
      </button>
      {myLocation && (
        <div style={{marginTop: '8px'}}>
          <p>
            <strong>My Latitude:</strong> {myLocation.lat} &nbsp; 
            <strong>My Longitude:</strong> {myLocation.lng} &nbsp; 
            <strong>Accuracy:</strong> {myLocation.acc}
          </p>
        </div>
      )}
      {coordinates && myLocation && (() => {
        const distance = haversineDistance(coordinates, myLocation);
        const withinRadius = distance <= RADIUS_METERS;
        return (
          <div style={{
            marginTop: '16px',
            padding: '16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9',
          }}>
            <p><strong>Distance:</strong> {distance.toFixed(2)} meters</p>
            <p style={{color: withinRadius ? 'green' : 'red', fontWeight: 'bold'}}>
              {withinRadius
                ? `Within the ${RADIUS_METERS}m radius`
                : `Outside the ${RADIUS_METERS}m radius`
              }
            </p>
          </div>
        );
      })()}
    </div>
  );
}

const MapContainer = observer(({scope}) => {
  return (
    <div>
      <TimeTrackingSubnav scope={scope}/>
      <Layout>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GeocodingMap/>
        </APIProvider>
      </Layout>
    </div>
  );
});

export default MapContainer;
